import { createContext, useContext, useEffect, useReducer } from 'react';

import { useMutation } from '@apollo/client';

import { Filter, MergeInput, StrategyState as State } from './types';
import {
  ARCHIVE_STRATEGY,
  CLONE_STRATEGY,
  DELETE_STRATEGY,
  // MERGE_STRATEGIES,
  RESUME_STRATEGY,
  SET_STRATEGY_ACTIVE,
  SET_STRATEGY_NAME,
  SET_STRATEGY_TAGS,
} from '../../http/apollo-queries';
import { Dashboard } from './Dashboard';
import { EditTags } from './EditTags';
import { RenameDuplicate } from './RenameDuplicate';
import { ArchiveResumeDelete } from './ArchiveResumeDelete';
import { GetStrategyData } from './GetStrategyData';
import { IStrategy } from '../../types/model';
import { useUserStrategies } from '../../context/UserStrategies';
import { useAuth } from '../../context/Auth';
import { useTutorialContext } from '../../context/Tutorial';
import { AdvancedSettings } from './AdvancedSettings';

export const STRATEGIES_OPTIONS = [
  'Strategies',
  'Compare',
  // 'Merge',
  'Align',
] as const;

export const OPTIONS_LIST_ELEMENTS = {
  'Edit tags': <EditTags />,
  Rename: <RenameDuplicate />,
  Duplicate: <RenameDuplicate />,
  Share: <p>TODO</p>,
  // Export: <GetStrategyData />,
  'Advanced Settings': <AdvancedSettings />,
  Archive: <ArchiveResumeDelete />,
  Resume: <ArchiveResumeDelete />,
  Delete: <ArchiveResumeDelete />,
};

const reducer = (state: State, updates: Partial<State>): State => {
  return { ...state, ...updates };
};

// this state will be updated by the 'reducer' function
const localState: State = {
  activeStrategies: [],

  archivedStrategies: [],

  // element for the modal
  element: undefined,

  // dropdown from ControlBar
  filter: 'All',

  inactiveStrategies: [],

  loadingStrategyActivationId: undefined,

  // the way the View is displayed
  mode: 'viewList',

  option: undefined,

  showAddWidgetModal: false,

  strategiesOption: STRATEGIES_OPTIONS[0],

  closeModal: () => {},
  dispatch: () => {},
  filterStrategies: () => {},
  handleStrategyActivation: () => {},
  handleOnSelect: () => {},
};
const StrategiesContext = createContext(localState);

const StrategiesProvider = ({ children }: { children: JSX.Element }) => {
  const { user } = useAuth();

  const {
    showTutorial: { STRATEGIES },
  } = user?.profile ?? { showTutorial: { STRATEGIES: false } };

  const {
    strategies: items,
    loading: loadingStrategies,
    refetch: refetchStrategies,
  } = useUserStrategies();

  const { handleTooltipIconVisibility } = useTutorialContext();

  const [state, dispatch] = useReducer(reducer, localState);
  const { filter: stateFilter } = state;

  useEffect(() => {
    STRATEGIES && dispatch({ mode: 'viewGraph' });
    // STRATEGIES && handleTooltipIconVisibility('STRATEGIES');
  }, [STRATEGIES]);

  const closeModal = () => {
    dispatch({ element: undefined, strategy: undefined, option: undefined });
  };

  // ControlBar dropdown
  const filterStrategies = (filter: Filter = stateFilter) => {
    const { activeStrategies, archivedStrategies, inactiveStrategies } =
      items.reduce(
        (obj, strategy) => ({
          ...obj,
          ...(strategy.active
            ? { activeStrategies: [...obj.activeStrategies, strategy] }
            : strategy.archived
            ? { archivedStrategies: [...obj.archivedStrategies, strategy] }
            : { inactiveStrategies: [...obj.inactiveStrategies, strategy] }),
        }),
        {
          activeStrategies: [],
          archivedStrategies: [],
          inactiveStrategies: [],
        } as {
          activeStrategies: IStrategy[];
          archivedStrategies: IStrategy[];
          inactiveStrategies: IStrategy[];
        }
      );

    dispatch({
      activeStrategies,
      archivedStrategies,
      filter,
      inactiveStrategies,
    });
  };

  const handleStrategyActivation: State['handleStrategyActivation'] = (
    strategyId: string,
    active: boolean
  ) => {
    // loading only the toggle of selected strategy
    dispatch({ loadingStrategyActivationId: strategyId });
    setStrategyActive({ variables: { strategyId, active } });
  };

  const handleOnSelect = (option: string, strategy: IStrategy) =>
    dispatch({
      element: OPTIONS_LIST_ELEMENTS[option],
      strategy,
      option,
    });

  useEffect(() => {
    filterStrategies();
  }, [items]);

  const onCompleted = () => {
    refetchStrategies().then(() => {
      state.element && closeModal();
      state.loadingStrategyActivationId &&
        dispatch({ loadingStrategyActivationId: undefined });
    });
  };

  // mutations
  const [setStrategyActive] = useMutation(SET_STRATEGY_ACTIVE, { onCompleted });

  const [setStrategyName, { loading: loadingChangeName }] = useMutation(
    SET_STRATEGY_NAME,
    { onCompleted }
  );

  const [setStrategyTags, { loading: loadingChangeTags }] = useMutation(
    SET_STRATEGY_TAGS,
    { onCompleted }
  );

  const [resumeStrategy, { loading: loadingResumeStrategy }] = useMutation(
    RESUME_STRATEGY,
    { onCompleted }
  );

  const [archiveStrategy, { loading: loadingArchivation }] = useMutation(
    ARCHIVE_STRATEGY,
    { onCompleted }
  );

  const [deleteStrategy, { loading: loadingDeletion }] = useMutation(
    DELETE_STRATEGY,
    { onCompleted }
  );

  const [cloneStrategy, { loading: loadingClone }] =
    useMutation(CLONE_STRATEGY);

  const handleAddWidgetModal = () =>
    dispatch({ showAddWidgetModal: !state.showAddWidgetModal });

  const value = {
    ...state,
    items,
    loadingArchivation,
    loadingChangeName,
    loadingClone,
    loadingDeletion,
    loadingChangeTags,
    loadingResumeStrategy,
    loadingStrategies,
    archiveStrategy,
    cloneStrategy,
    closeModal,
    deleteStrategy,
    dispatch,
    filterStrategies,
    handleAddWidgetModal,
    handleOnSelect,
    handleStrategyActivation,
    resumeStrategy,
    setStrategyName,
    setStrategyTags,
    refetchStrategies,
  };

  return (
    <StrategiesContext.Provider value={value}>
      {children}
    </StrategiesContext.Provider>
  );
};

export const StrategiesPage = () => (
  <StrategiesProvider>
    <Dashboard />
  </StrategiesProvider>
);

export const useStrategiesContext = () => useContext(StrategiesContext);
